import { useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";

/**
 * Mounted once at the dashboard root. The first time a user lands there,
 * shows a non-intrusive toast inviting them to take the 3-min interactive demo.
 * State is persisted in localStorage so we don't nag returning users.
 *
 * Skipped on /dashboard/video/* (would cover the transcript panel during a demo).
 */
export default function FirstLoginPrompt() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!user || !user.email) return;
    if (location.pathname.startsWith("/dashboard/video/")) return;
    const key = `knoria_first_login_seen_${user.email}`;
    if (localStorage.getItem(key)) return;

    // Eric 2026-05-06 — Marquer comme "vu" IMMÉDIATEMENT dès qu'on schedule le toast
    // pour éviter l'accumulation sur reload/navigation rapide. L'utilisateur peut toujours
    // cliquer "Commencer la visite" ou ignorer, mais le popup ne réapparaît plus.
    localStorage.setItem(key, "1");

    const t = setTimeout(() => {
      toast.custom(
        (id) => (
          <div
            data-testid="first-login-prompt"
            className="bg-[#0a4d3a] text-white rounded-xl shadow-2xl border border-[#c89b3c]/40 p-5 max-w-md"
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-[#c89b3c]/20 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-5 h-5 text-[#c89b3c]" />
              </div>
              <div className="flex-1">
                <div className="font-serif text-lg mb-1">
                  Bienvenue sur Knoria
                </div>
                <p className="text-sm text-white/80 mb-4">
                  Nouveau ? Démarrez avec une visite guidée de 3 minutes pour
                  voir comment transformer vos vidéos en intelligence collective.
                </p>
                <div className="flex gap-2">
                  <button
                    data-testid="first-login-tour-btn"
                    onClick={() => {
                      toast.dismiss(id);
                      // Tâche 1396 — Envoyait vers `/demo`, qui est la page
                      // PUBLIQUE de démo commerciale : la personne qui vient
                      // de se connecter atterrissait sur l'argumentaire de
                      // vente au lieu de la visite guidée du produit.
                      navigate("/visite-guidee");
                    }}
                    className="bg-[#c89b3c] hover:bg-[#b8893c] text-[#0a4d3a] text-sm font-semibold px-4 py-2 rounded-md"
                  >
                    Commencer la visite
                  </button>
                  <button
                    data-testid="first-login-dismiss-btn"
                    onClick={() => toast.dismiss(id)}
                    className="text-white/60 hover:text-white text-sm px-3 py-2"
                  >
                    Plus tard
                  </button>
                </div>
              </div>
            </div>
          </div>
        ),
        { id: "knoria-first-login-toast", duration: 30000, position: "bottom-right" }
      );
    }, 2500);
    return () => clearTimeout(t);
  }, [user, navigate, location.pathname]);

  return null;
}
